import React, { memo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import { useUser } from 'Context/User';
import styles from 'Components/PdfFormTemplates/pdfFormTemplates.module.css';
import { myProjectsSelector } from 'Containers/Projects/selectors';
import { listUserProjects } from 'Containers/Projects/actions';
import { createPdfFormSubmission, sharePdfFormSubmission, fetchProjectClaims } from './actions';
import { pdfFormSubmissionSelector } from './selectors';

interface Props {
  isOpen: boolean;
  template: any;
  onClose: () => void;
}

function SendFormModal({ isOpen, template, onClose }: Props) {
  const dispatch = useDispatch();
  const { id: userId } = useUser();
  const myProjects = useSelector(myProjectsSelector, areEqual);
  const submission = useSelector(pdfFormSubmissionSelector, areEqual);

  const [projectId, setProjectId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [sendMethod, setSendMethod] = useState<'email_and_sms' | 'email' | 'sms'>('email_and_sms');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<'form' | 'success'>('form');

  const formatPhone = useCallback((value: string) => {
    let digits = value.replace(/\D/g, '');
    let prefix = '';
    // Preserve country code '1' prefix
    if (digits.length === 11 && digits.startsWith('1')) {
      prefix = '+1 ';
      digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);
    if (digits.length <= 3) return `${prefix}${digits}`;
    if (digits.length <= 6) return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }, []);

  const rawPhone = clientPhone.replace(/\D/g, '');

  const projects = myProjects?.data || [];

  const getProjectLabel = useCallback(
    (p: any) => p.address?.address || p.uid || `Project #${p.id}`,
    []
  );

  const filteredProjects = projects.filter((p: any) => {
    if (!projectSearch.trim()) return true;
    const label = getProjectLabel(p).toLowerCase();
    return label.includes(projectSearch.toLowerCase());
  });

  // Fetch projects when modal opens
  useEffect(() => {
    if (isOpen && userId && projects.length === 0) {
      dispatch(listUserProjects(userId) as any);
    }
  }, [isOpen, userId]);

  // Auto-fill client info from project claims when project is selected
  const onProjectSelect = useCallback(
    async (project: any) => {
      setProjectId(String(project.id));
      setProjectSearch(getProjectLabel(project));
      setProjectDropdownOpen(false);

      const claims = await dispatch(fetchProjectClaims(project.id) as any);
      if (claims?.length > 0) {
        const claim = claims[0];
        if (claim.policy_holder) setClientName(claim.policy_holder);
        if (claim.policy_holder_email) setClientEmail(claim.policy_holder_email);
        if (claim.policy_holder_phone) setClientPhone(formatPhone(claim.policy_holder_phone));
      }
    },
    [dispatch, getProjectLabel]
  );

  const handleClose = useCallback(() => {
    setProjectId('');
    setProjectSearch('');
    setProjectDropdownOpen(false);
    setClientName('');
    setClientEmail('');
    setClientPhone('');
    setSendMethod('email_and_sms');
    setSubmitting(false);
    setError('');
    setStep('form');
    onClose();
  }, [onClose]);

  const onSubmit = useCallback(async () => {
    const needsEmail = sendMethod === 'email' || sendMethod === 'email_and_sms';
    const needsSms = sendMethod === 'sms' || sendMethod === 'email_and_sms';
    if (!template?.id || !projectId || !clientName.trim()) return;
    if (needsEmail && !clientEmail.trim()) return;
    if (needsSms && !rawPhone) return;
    setSubmitting(true);
    setError('');

    const result = await dispatch(
      createPdfFormSubmission({
        pdf_form_template_id: template.id,
        project_id: Number(projectId),
        client_name: clientName.trim(),
        client_email: clientEmail.trim() || undefined,
        client_phone: rawPhone || undefined,
      }) as any
    );

    if (!result) {
      setError('Failed to create submission. Please try again.');
      setSubmitting(false);
      return;
    }

    // Send share (email/SMS)
    const shareData: { email?: string; phone?: string } = {};
    if (needsEmail && clientEmail) shareData.email = clientEmail;
    if (needsSms && rawPhone) shareData.phone = rawPhone;

    if (shareData.email || shareData.phone) {
      const shareResult = await dispatch(sharePdfFormSubmission(result.id, shareData) as any);
      if (!shareResult) {
        setError('Submission created but failed to send. Please try sharing again.');
        setSubmitting(false);
        return;
      }
    }

    setSubmitting(false);
    handleClose();
  }, [template, projectId, clientName, clientEmail, rawPhone, sendMethod, dispatch, handleClose]);

  if (!isOpen || !template) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.modalOverlay} onClick={handleClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {step === 'form' ? (
          <>
            <h2>Send Form to Client</h2>
            <p style={{ fontSize: 14, color: '#6c757d', margin: '0 0 16px' }}>
              {'Template: '}
              <strong>{template.name}</strong>
            </p>

            <div className={styles.formGroup}>
              <label>Project</label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => {
                    setProjectSearch(e.target.value);
                    setProjectId('');
                    setProjectDropdownOpen(true);
                  }}
                  onFocus={() => setProjectDropdownOpen(true)}
                  onBlur={() => setProjectDropdownOpen(false)}
                  placeholder="Search projects..."
                />
                {projectDropdownOpen && (
                  <div className={styles.projectDropdown}>
                    {filteredProjects.length > 0 ? (
                      filteredProjects.map((p: any) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div
                          key={p.id}
                          className={styles.projectDropdownItem}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onProjectSelect(p);
                          }}
                        >
                          {getProjectLabel(p)}
                        </div>
                      ))
                    ) : (
                      <div className={styles.projectDropdownEmpty}>No projects found</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label>Client Name</label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="John Doe"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Client Email</label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>

            <div className={styles.formGroup}>
              <label>{`Client Phone${sendMethod === 'email' ? ' (optional)' : ''}`}</label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Send via</label>
              <select
                value={sendMethod}
                onChange={(e) => setSendMethod(e.target.value as 'email_and_sms' | 'email' | 'sms')}
              >
                <option value="email_and_sms">Email and SMS</option>
                <option value="email">Email only</option>
                <option value="sms">SMS only</option>
              </select>
            </div>

            {error && (
              <p style={{ color: '#dc3545', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
            )}

            <div className={styles.modalActions}>
              <button type="button" className={styles.btnSecondary} onClick={handleClose}>
                Cancel
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={onSubmit}
                disabled={
                  !projectId ||
                  !clientName.trim() ||
                  ((sendMethod === 'email' || sendMethod === 'email_and_sms') && !clientEmail.trim()) ||
                  ((sendMethod === 'sms' || sendMethod === 'email_and_sms') && !rawPhone) ||
                  submitting
                }
              >
                {submitting ? 'Sending...' : 'Create & Send'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>Form Sent</h2>
            <p style={{ fontSize: 14, color: '#495057' }}>
              {`The form has been created and sent to `}
              <strong>{clientName}</strong>
              .
            </p>
            {submission?.short_url && (
              <div className={styles.formGroup}>
                <label>Signing Link</label>
                <input type="text" value={submission.short_url || submission.url} readOnly style={{ background: '#f8f9fa' }} />
              </div>
            )}
            <div className={styles.modalActions}>
              <button type="button" className={styles.btnPrimary} onClick={handleClose}>
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const SendFormModalMemo = memo(SendFormModal, areEqual);
export { SendFormModalMemo as SendFormModal };
